import { createMMKV } from 'react-native-mmkv';
import type { Configuration, MMKV } from 'react-native-mmkv';

import { logger as LOG } from '@garnish/logger';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a key-value store whose writes cannot crash the app.
 *
 * `react-native-mmkv` offers no non-throwing write: `set` throws when the
 * native store cannot grow its backing file, which is what happens once a
 * device runs out of storage. Everything we keep in these stores is a
 * convenience, so a rejected write costs that one value, not the session.
 *
 * Only `set` is replaced. The store is a Nitro `HybridObject` that keeps its
 * methods on the prototype and its data in `NativeState`, so it is proxied
 * rather than copied — a spread would drop every method on a device while
 * still passing against the plain-object mock used in tests.
 */
export function createStorage(params: Configuration): MMKV {
  const storage = createMMKV(params);

  function set(key: string, value: boolean | number | string | ArrayBuffer) {
    try {
      storage.set(key, value);
    } catch (error) {
      logger.warn('Write Rejected', { id: params.id, key, error: formatError(error) });
    }
  }

  return new Proxy(storage, {
    get(target, property) {
      if (property === 'set') {
        return set;
      }

      const value = Reflect.get(target, property, target);

      // Native methods resolve their `NativeState` from `this`, which would
      // otherwise be the proxy.
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatError(error: unknown) {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }

  return String(error);
}

// ─── Logger ──────────────────────────────────────────────────────────────────

const logger = LOG.extend('MMKV Storage');

LOG.enable('MMKV Storage');
